import React, { useEffect, useState } from 'react'
import { TextField, Typography, Tooltip, Autocomplete, createFilterOptions } from '@mui/material'

const REQUEST_TIMEOUT = 300

/**
 * @typedef {object} AutocompleteInputChangeEvent
 * @property {Event} event - El evento original del navegador.
 * @property {any} value - El valor seleccionado (objeto o array de objetos si multiple es true).
 * @property {string} name - El nombre del campo.
 */

/**
 * @typedef {object} AutocompleteInputProps
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} optionKey - La clave del objeto de opción que se usa como valor y etiqueta.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [placeholder=''] - Texto de marcador de posición que aparece cuando el campo está vacío.
 * @property {string} [requestPath] - Ruta de la petición para obtener opciones dinámicamente.
 * @property {any} [value] - El valor actual seleccionado (objeto o array de objetos si multiple es true).
 * @property {any[]} [values=[]] - Array de opciones disponibles para seleccionar.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {boolean} [disabled=false] - Si es true, el campo está deshabilitado.
 * @property {boolean} [multiple=false] - Si es true, permite seleccionar múltiples opciones.
 * @property {function} [labelOption] - Función personalizada para obtener la etiqueta de una opción.
 * @property {function} [renderOption] - Función personalizada para renderizar el contenido de una opción.
 * @property {number} [inputMinLength=3] - Longitud mínima del texto de entrada para activar la búsqueda.
 * @property {boolean} [tooltipEllipsis=false] - Si es true, muestra tooltips con el texto completo para opciones truncadas.
 * @property {number} [requestTimeout=300] - Tiempo de espera en ms antes de ejecutar la búsqueda con onType.
 * @property {function(AutocompleteInputChangeEvent): void} onChange - Callback ejecutado cuando cambia la selección.
 * @property {function(string): Promise<void>} [onType] - Callback ejecutado cuando el usuario escribe (para búsquedas dinámicas).
 */

/**
 * Un componente de autocompletado que permite búsqueda y selección de opciones.
 *
 * Proporciona funcionalidad de búsqueda con autocompletado, soporte para carga dinámica
 * de opciones mediante API, selección simple o múltiple, y personalización del renderizado
 * de opciones. Incluye debouncing para búsquedas dinámicas y tooltips para opciones largas.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [ciudad, setCiudad] = useState(null);
 *   const [ciudades, setCiudades] = useState([]);
 *
 *   const handleChange = ({ event, value, name }) => {
 *     console.log(`El campo '${name}' ahora es:`, value);
 *     setCiudad(value);
 *   };
 *
 *   const handleSearch = async (searchText) => {
 *     const response = await fetch(`/api/ciudades?q=${searchText}`);
 *     const data = await response.json();
 *     setCiudades(data);
 *   };
 *
 *   return (
 *     <AutocompleteInput
 *       title="Ciudad"
 *       name="ciudad"
 *       optionKey="nombre"
 *       value={ciudad}
 *       values={ciudades}
 *       onChange={handleChange}
 *       onType={handleSearch}
 *       required
 *       placeholder="Buscar ciudad..."
 *       inputMinLength={3}
 *     />
 *   );
 * };
 *
 * @param {AutocompleteInputProps} props - Las props del componente AutocompleteInput.
 */
export const AutocompleteInput = ({
  name,
  optionKey,
  title,
  placeholder = '',
  requestPath,
  onChange,
  onType,
  values = [],
  required = false,
  disabled = false,
  multiple = false,
  labelOption,
  inputMinLength = 3,
  tooltipEllipsis = false,
  requestTimeout = REQUEST_TIMEOUT,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  // Sincronizar inputValue con el valor seleccionado
  useEffect(() => {
    if (!multiple && props.value) {
      const displayValue = getOptionLabel(props.value)
      setInputValue(displayValue)
    } else {
      setInputValue('')
    }
  }, [props.value])

  useEffect(() => {
    if (onType && inputValue && inputValue.length >= inputMinLength) {
      setLoading(true)

      const timer = setTimeout(async () => {
        try {
          const currentInputValue = inputValue
          if (inputValue === currentInputValue) {
            await onType(inputValue)
            setLoading(false)
          }
        } catch {
          setLoading(false)
        }
      }, requestTimeout)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [inputValue]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderTooltipEllipsisOption = (props, option) => {
    return (
      <li {...props} key={option[optionKey] || option}>
        <Tooltip
          placement="top-start"
          title={option[optionKey]}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Typography variant="body2">{option[optionKey]}</Typography>
        </Tooltip>
      </li>
    )
  }

  const renderDefaultOption = (comp_props, option) => {
    let displayValue = props.renderOption ? props.renderOption(option) : option[optionKey]

    if (displayValue == null || displayValue === '') {
      displayValue = typeof option === 'string' ? option : option[optionKey] || ''
    }

    const displayStr = String(displayValue || '')
    const keyValue = option[optionKey] || option.key || displayStr || Math.random()

    return (
      <li {...comp_props} key={keyValue}>
        {displayStr}
      </li>
    )
  }

  const getOptionLabel =
    labelOption ||
    ((option) => {
      if (!option) return ''
      return renderValue(option)
    })

  const renderValue = (option) => {
    if (props.renderOption) {
      const rendered = props.renderOption(option)
      return rendered != null ? String(rendered) : ''
    }
    const value = option[optionKey]
    if (value != null && typeof value === 'string') return value
    if (value != null) return String(value)
    if (typeof option === 'string') return option
    return ''
  }

  const handleChange = (event, value) => {
    onChange({ event, value, name })
  }

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue)
    } else if (reason === 'clear') {
      setInputValue('')
    }
  }

  return (
    <Autocomplete
      {...props}
      id={`${name}-autocomplete-input`}
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={values}
      fullWidth
      getOptionLabel={getOptionLabel}
      renderOption={tooltipEllipsis ? renderTooltipEllipsisOption : renderDefaultOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disabled={disabled}
      multiple={multiple}
      filterOptions={createFilterOptions({ stringify: renderValue })}
      renderInput={(params) => (
        <>
          <Typography variant="subtitle1" gutterBottom>
            {title}
            {required ? <span style={{ color: '#B3272C' }}> *</span> : null}
          </Typography>
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            slotProps={{
              input: {
                ...params.InputProps,
                required,
              },
            }}
          />
        </>
      )}
      loading={loading}
    />
  )
}
